import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App, Button, Col, Row, Select, Space, Typography } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, FilterOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import {
  getPropertyAreasForRole, getSalesDetailAnalytics, getSalesDetailPipeline, getSalesDetailSummary, getSalesDetailTrends
} from '../../services/dashboardService';
import {
  PerformanceSummary, SalesFunnelData, SalesLeadSourceDistribution, SalesMonthlyTrend, SalesPipelineDistribution, SalesProfile, SalesRecentAchievement, SalesSLAResponsiveness, SourceMonthlyTrend
} from '../../types/dashboard';
import {
  glassCardStyle, glassTableContainerStyle, MONTH_NAMES
} from './utils';

// Import Modular Components
import SalesDetailProfileCard from './components/SalesDetailProfileCard';
import SalesDetailSummaryCards from './components/SalesDetailSummaryCards';
import SalesDetailPipelineChart from './components/SalesDetailPipelineChart';
import SalesDetailFunnelCard from './components/SalesDetailFunnelCard';
import SalesDetailTrendsChart from './components/SalesDetailTrendsChart';
import SalesDetailSlaCard from './components/SalesDetailSlaCard';
import SalesDetailSourcesChart from './components/SalesDetailSourcesChart';
import SalesDetailRecentAchievements from './components/SalesDetailRecentAchievements';

const { Title, Text } = Typography;

export const SalesPerformanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  // Filters State
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // 0 = Full Year Accumulative
  const [selectedAreaId, setSelectedAreaId] = useState<string>(''); // empty = All Areas

  // Data States
  const [profile, setProfile] = useState<SalesProfile | null>(null);
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [pipeline, setPipeline] = useState<SalesPipelineDistribution | null>(null);
  const [funnel, setFunnel] = useState<SalesFunnelData | null>(null);
  const [trends, setTrends] = useState<SalesMonthlyTrend[] | null>(null);
  const [sla, setSla] = useState<SalesSLAResponsiveness | null>(null);
  const [sources, setSources] = useState<SalesLeadSourceDistribution[] | null>(null);
  const [achievements, setAchievements] = useState<SalesRecentAchievement[] | null>(null);
  const [sourceMonthlyTrends, setSourceMonthlyTrends] = useState<SourceMonthlyTrend[] | null>(null);
  const [leadsByInterestReason, setLeadsByInterestReason] = useState<any[] | null>(null);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);

  // Loading States
  const [loadingFilters, setLoadingFilters] = useState<boolean>(true);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
  const [loadingPipeline, setLoadingLoadingPipeline] = useState<boolean>(true);
  const [loadingTrends, setLoadingTrends] = useState<boolean>(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState<boolean>(true);

  // Fetch Available Property Areas for Filter
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        setLoadingFilters(true);
        const res = await getPropertyAreasForRole();
        if (res?.status === 'success') {
          setPropertyAreas(res.data || []);
        }
      } catch (err) {
        console.error('Failed to load filter property areas:', err);
        message.error('Gagal memuat filter area properti.');
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchAreas();
  }, [message]);

  // Fetch Endpoint 1: Summary & Profile
  useEffect(() => {
    if (!id) return;
    const fetchSummary = async () => {
      setLoadingSummary(true);
      try {
        const params: any = { periode: selectedYear };
        if (selectedMonth > 0) params.month = selectedMonth;
        if (selectedAreaId) params.property_area_id = selectedAreaId;

        const res = await getSalesDetailSummary(id, params);
        if (res?.status === 'success') {
          setProfile(res.data?.profile);
          setSummary(res.data?.summary);
        } else {
          message.error(res?.message || 'Gagal memuat profil dan ringkasan KPI.');
        }
      } catch (err: any) {
        console.error('Error fetching sales detail summary:', err);
        if (err.response?.status === 403) {
          message.error('Anda tidak memiliki akses ke detail performa agen sales ini.');
          navigate('/performa/dashboard');
        } else {
          message.error('Gagal memuat profil dan ringkasan KPI.');
        }
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchSummary();
  }, [id, selectedYear, selectedMonth, selectedAreaId, navigate, message]);

  // Fetch Endpoint 2: Pipeline & Funnel
  useEffect(() => {
    if (!id) return;
    const fetchPipeline = async () => {
      setLoadingLoadingPipeline(true);
      try {
        const params: any = { periode: selectedYear };
        if (selectedMonth > 0) params.month = selectedMonth;
        if (selectedAreaId) params.property_area_id = selectedAreaId;

        const res = await getSalesDetailPipeline(id, params);
        if (res?.status === 'success') {
          setPipeline(res.data?.pipeline);
          setFunnel(res.data?.funnel);
        } else {
          message.error(res?.message || 'Gagal memuat statistik pipeline & corong konversi.');
        }
      } catch (err) {
        console.error('Error fetching sales detail pipeline:', err);
        message.error('Gagal memuat statistik pipeline & corong konversi.');
      } finally {
        setLoadingLoadingPipeline(false);
      }
    };
    fetchPipeline();
  }, [id, selectedYear, selectedMonth, selectedAreaId, message]);

  // Fetch Endpoint 3: Trends (No month parameter)
  useEffect(() => {
    if (!id) return;
    const fetchTrends = async () => {
      setLoadingTrends(true);
      try {
        const params: any = { periode: selectedYear };
        if (selectedAreaId) params.property_area_id = selectedAreaId;

        const res = await getSalesDetailTrends(id, params);
        if (res?.status === 'success') {
          setTrends(res.data?.monthly_trends);
        } else {
          message.error(res?.message || 'Gagal memuat grafik tren bulanan.');
        }
      } catch (err) {
        console.error('Error fetching sales detail trends:', err);
        message.error('Gagal memuat grafik tren bulanan.');
      } finally {
        setLoadingTrends(false);
      }
    };
    fetchTrends();
  }, [id, selectedYear, selectedAreaId, message]);

  // Fetch Endpoint 4: Analytics
  useEffect(() => {
    if (!id) return;
    const fetchAnalytics = async () => {
      setLoadingAnalytics(true);
      try {
        const params: any = { periode: selectedYear };
        if (selectedMonth > 0) params.month = selectedMonth;
        if (selectedAreaId) params.property_area_id = selectedAreaId;

        const res = await getSalesDetailAnalytics(id, params);
        if (res?.status === 'success') {
          setSla(res.data?.sla_responsiveness);
          setSources(res.data?.leads_by_source);
          setAchievements(res.data?.recent_achievements);
          setSourceMonthlyTrends(res.data?.source_monthly_trends || []);
          setLeadsByInterestReason(res.data?.leads_by_interest_reason || []);
        } else {
          message.error(res?.message || 'Gagal memuat analisis data SLA & pencapaian.');
        }
      } catch (err) {
        console.error('Error fetching sales detail analytics:', err);
        message.error('Gagal memuat analisis data SLA & pencapaian.');
      } finally {
        setLoadingAnalytics(false);
      }
    };
    fetchAnalytics();
  }, [id, selectedYear, selectedMonth, selectedAreaId, message]);

  return (
    <div style={{
      padding: '24px',
      minHeight: '100vh',
      background: 'radial-gradient(circle at 50% 0%, #f8fafc 0%, #e2e8f0 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Dynamic Style Injection for Premium Interactive Animations */}
      <style>{`
        .glass-card-interactive {
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .glass-card-interactive:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 40px rgba(123, 170, 24, 0.08), 0 4px 12px rgba(0, 0, 0, 0.02) !important;
          border-color: rgba(123, 170, 24, 0.35) !important;
        }
        
        .glass-table-container {
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .glass-table-container:hover {
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.04) !important;
          border-color: rgba(255, 255, 255, 0.45) !important;
        }
        
        .funnel-stage-item {
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .funnel-stage-item:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06) !important;
          filter: brightness(1.03);
        }
        
        .glass-table .ant-table-tbody > tr:hover > td {
          background: rgba(123, 170, 24, 0.04) !important;
          transition: background 0.2s ease;
        }
        
        .glass-table .ant-table-thead > tr > th {
          background: rgba(248, 250, 252, 0.65) !important;
          font-weight: 700;
        }

        /* Make active sorter carets highly visible, vibrant, and larger */
        .glass-table .ant-table-column-sorter-up.active .anticon,
        .glass-table .ant-table-column-sorter-down.active .anticon {
          color: #1890ff !important;
          font-size: 13px !important;
          filter: drop-shadow(0 1px 3px rgba(24, 144, 255, 0.35));
          transform: scale(1.25);
          transition: all 0.2s ease;
        }

        /* Customize inactive caret arrow colors to be softer but readable */
        .glass-table .ant-table-column-sorter-inner {
          gap: 2px;
        }
        .glass-table .ant-table-column-sorter-up:not(.active) .anticon,
        .glass-table .ant-table-column-sorter-down:not(.active) .anticon {
          color: #94a3b8 !important;
          opacity: 0.55;
        }
      `}</style>

      {/* Decorative Floating Ambient Orbs (Hardware Accelerated Blur) */}
      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '-150px',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(123, 170, 24, 0.12) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(60px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
      <div style={{
        position: 'absolute',
        top: '30%',
        right: '-200px',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(24, 144, 255, 0.08) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(80px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        left: '10%',
        width: '450px',
        height: '450px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(250, 173, 20, 0.06) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(70px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />

      {/* Main Content Area */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Top Banner Navigation & Filters */}
        <div className="glass-table-container" style={{ ...glassTableContainerStyle, marginBottom: '20px', padding: '16px 24px' }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space size={12}>
                <Button
                  shape="circle"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/performa/dashboard')}
                  style={{ background: 'rgba(255, 255, 255, 0.6)', border: '1px solid rgba(255, 255, 255, 0.3)' }}
                />
                <div>
                  <Title level={3} style={{ margin: 0, color: '#0f172a', fontWeight: 800 }}>
                    Detail Kinerja Sales Agent
                  </Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Kartu Rapor Digital & Analisis Produktivitas Agen Sales
                  </Text>
                </div>
              </Space>
            </Col>

            {/* Global Controls Filter */}
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Space size={12} wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
                {/* Filter Area */}
                <Select
                  placeholder="Semua Kawasan"
                  value={selectedAreaId || undefined}
                  onChange={(val) => setSelectedAreaId(val || '')}
                  loading={loadingFilters}
                  allowClear
                  style={{ width: 160 }}
                  dropdownStyle={{ backdropFilter: 'blur(8px)' }}
                  suffixIcon={<EnvironmentOutlined style={{ color: '#7BAA18' }} />}
                >
                  {propertyAreas.map(area => (
                    <Select.Option key={area.id} value={area.id}>{area.name}</Select.Option>
                  ))}
                </Select>

                {/* Filter Month */}
                <Select
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  style={{ width: 140 }}
                  suffixIcon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                >
                  <Select.Option value={0}>Setahun Penuh</Select.Option>
                  {MONTH_NAMES.map((name, i) => (
                    <Select.Option key={i + 1} value={i + 1}>{name}</Select.Option>
                  ))}
                </Select>

                {/* Filter Year */}
                <Select
                  value={selectedYear}
                  onChange={setSelectedYear}
                  style={{ width: 100 }}
                  suffixIcon={<FilterOutlined />}
                >
                  {[2024, 2025, 2026, 2027].map(y => {
                    return <Select.Option key={y} value={y}>{y}</Select.Option>;
                  })}
                </Select>
              </Space>
            </Col>
          </Row>
        </div>

        <Row gutter={[20, 20]}>
          {/* Left Side: Profile Card */}
          <Col xs={24} lg={6}>
            <SalesDetailProfileCard 
              profile={profile} 
              loading={loadingSummary} 
              glassCardStyle={glassCardStyle} 
            />
          </Col>

          {/* Right Side: KPI and Charts */}
          <Col xs={24} lg={18}>
            {/* Card Summary Row */}
            <SalesDetailSummaryCards 
              summary={summary} 
              loading={loadingSummary} 
              glassCardStyle={glassCardStyle} 
            />

            <Row gutter={[16, 16]}>
              {/* Donut Chart Pipeline */}
              <Col xs={24} md={12}>
                <SalesDetailPipelineChart 
                  pipeline={pipeline} 
                  loading={loadingPipeline} 
                  glassCardStyle={glassCardStyle} 
                />
              </Col>

              {/* Custom Funnel Conversion Card */}
              <Col xs={24} md={12}>
                <SalesDetailFunnelCard 
                  funnel={funnel} 
                  loading={loadingPipeline} 
                  glassCardStyle={glassCardStyle} 
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* MoM Correlation Chart Setahun Penuh */}
        <SalesDetailTrendsChart 
          trends={trends} 
          loading={loadingTrends} 
          glassTableContainerStyle={glassTableContainerStyle} 
        />

        {/* Bottom Panel: Analytics, SLA Responsiveness, Sources, & Recent Achievements */}
        <Row gutter={[20, 20]} style={{ marginTop: '20px' }}>
          <Col xs={24} md={12}>
            <SalesDetailSlaCard 
              sla={sla} 
              slaComplianceRate={summary?.sla_compliance_rate} 
              loading={loadingAnalytics} 
              glassCardStyle={glassCardStyle} 
            />
          </Col>

          {/* Media Sources Contribution & Trends */}
          <Col xs={24} md={12}>
            <SalesDetailSourcesChart 
              sources={sources} 
              sourceMonthlyTrends={sourceMonthlyTrends}
              leadsByInterestReason={leadsByInterestReason}
              loading={loadingAnalytics} 
              glassCardStyle={glassCardStyle} 
            />
          </Col>
        </Row>

        {/* Recent Success Transactions (Approved OK) */}
        <SalesDetailRecentAchievements 
          achievements={achievements} 
          loading={loadingAnalytics} 
          glassTableContainerStyle={glassTableContainerStyle} 
        />
      </div>
    </div>
  );
};

export default SalesPerformanceDetail;
